import { useEffect, useRef, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { useWindowManager } from '@/lib/context/WindowManagerContext';
import apiClient from '@/lib/api/client';

// ── File Explorer (#67 v1) ──────────────────────────────────────────────────
// A Windows-like folder view of the business: Clients are folders, each opens
// to its Jobs, each job opens to Invoices / Quotes / Documents, and the leaves
// are the actual records — click to open. Lazy-loaded per folder. The full
// draggable-window desktop OS (wallpaper, taskbar, multi-window) is staged.

type NodeType = 'client' | 'job' | 'group' | 'invoice' | 'quote' | 'document';
interface Node {
  key: string; type: NodeType; label: string; sub?: string;
  clientId?: number; jobId?: number; group?: 'invoices' | 'quotes' | 'documents';
  open?: boolean; loading?: boolean; children?: Node[]; leaf?: boolean;
  href?: string; fileUrl?: string;
}

const ICON: Record<NodeType, string> = {
  client: 'folder_shared', job: 'folder', group: 'folder_open',
  invoice: 'receipt_long', quote: 'request_quote', document: 'draft',
};

const money = (n: unknown) => n != null ? `$${Number(n).toLocaleString('en-CA', { minimumFractionDigits: 2 })}` : '';

export const FileExplorerPage = () => {
  const { openWindow } = useWindowManager();
  const [roots, setRoots] = useState<Node[]>([]);
  const [selected, setSelected] = useState<Node | null>(null);
  // #72 desktop shell — draggable/minimizable explorer window + taskbar
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const [minimized, setMinimized] = useState(false);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const resizeRef = useRef<{ x: number; y: number; w: number; h: number } | null>(null);
  const onResizeDown = (e: React.PointerEvent) => {
    e.stopPropagation();
    const el = (e.currentTarget.parentElement as HTMLElement);
    resizeRef.current = { x: e.clientX, y: e.clientY, w: size?.w ?? el.offsetWidth, h: size?.h ?? el.offsetHeight };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onResizeMove = (e: React.PointerEvent) => {
    if (!resizeRef.current) return;
    const r = resizeRef.current;
    setSize({ w: Math.max(320, r.w + (e.clientX - r.x)), h: Math.max(260, r.h + (e.clientY - r.y)) });
  };
  const onResizeUp = () => { resizeRef.current = null; };
  const dragRef = useRef<{ dx: number; dy: number } | null>(null);
  const onTitleDown = (e: React.PointerEvent) => {
    const start = pos ?? { x: 0, y: 0 };
    dragRef.current = { dx: e.clientX - start.x, dy: e.clientY - start.y };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onTitleMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    setPos({ x: e.clientX - dragRef.current.dx, y: e.clientY - dragRef.current.dy });
  };
  const onTitleUp = () => { dragRef.current = null; };

  const LAUNCHERS: { icon: string; label: string; href: string }[] = [
    { icon: 'folder_open', label: 'Files', href: '/explorer' },
    { icon: 'mail', label: 'Mail', href: '/mail' },
    { icon: 'groups', label: 'Clients', href: '/clients' },
    { icon: 'request_quote', label: 'Quotes', href: '/quotes' },
    { icon: 'calendar_month', label: 'Planner', href: '/planner' },
    { icon: 'description', label: 'Docs', href: '/documents' },
  ];

  useEffect(() => {
    apiClient.get('/clients').then(r => {
      const d = r.data?.data ?? r.data;
      const list = (Array.isArray(d) ? d : d?.clients ?? []) as { id: number; name: string; company?: string }[];
      setRoots(list.map(c => ({
        key: `c${c.id}`, type: 'client', label: c.name || c.company || `Client ${c.id}`,
        sub: c.company && c.company !== c.name ? c.company : undefined, clientId: c.id,
      })));
    }).catch(() => {});
  }, []);

  const mutate = (key: string, fn: (n: Node) => Node) => {
    const walk = (nodes: Node[]): Node[] => nodes.map(n =>
      n.key === key ? fn(n) : n.children ? { ...n, children: walk(n.children) } : n);
    setRoots(rs => walk(rs));
  };

  const loadChildren = async (n: Node): Promise<Node[]> => {
    if (n.type === 'client') {
      const r = await apiClient.get(`/clients/${n.clientId}`).catch(() => null);
      const jobs = (r?.data?.data?.jobs ?? []) as { id: number; title: string; job_number?: string; status?: string }[];
      return jobs.map(j => ({
        key: `j${j.id}`, type: 'job', label: j.title || `Job ${j.job_number || j.id}`,
        sub: [j.job_number, j.status].filter(Boolean).join(' · '), clientId: n.clientId, jobId: j.id,
      }));
    }
    if (n.type === 'job') {
      return (['invoices', 'quotes', 'documents'] as const).map(g => ({
        key: `${g}-j${n.jobId}`, type: 'group', group: g, clientId: n.clientId, jobId: n.jobId,
        label: g.charAt(0).toUpperCase() + g.slice(1),
      }));
    }
    if (n.type === 'group') {
      if (n.group === 'invoices') {
        const r = await apiClient.get(`/invoices?job_id=${n.jobId}`).catch(() => null);
        const d = r?.data?.data; const list = (Array.isArray(d) ? d : d?.invoices ?? []) as Record<string, unknown>[];
        return list.map(i => ({
          key: `inv${i.id}`, type: 'invoice', leaf: true, label: `Invoice ${i.invoice_number}`,
          sub: `${money(i.total)} · ${i.status}`,
          href: `/clients/${n.clientId}/jobs/${n.jobId}/invoices/${i.id}`,
        }));
      }
      if (n.group === 'quotes') {
        const r = await apiClient.get(`/quotes?job_id=${n.jobId}`).catch(() => null);
        const d = r?.data?.data; const list = (Array.isArray(d) ? d : d?.quotes ?? []) as Record<string, unknown>[];
        return list.map(q => ({
          key: `qt${q.id}`, type: 'quote', leaf: true, label: `${q.quote_number || 'Quote'}`,
          sub: `${money(q.total)}${q.status ? ` · ${q.status}` : ''}`,
          href: `/clients/${n.clientId}/jobs/${n.jobId}/quotes/${q.id}`,
        }));
      }
      const r = await apiClient.get('/documents/all').catch(() => null);
      const all = (r?.data?.data ?? []) as Record<string, unknown>[];
      return all.filter(d => d.job_id === n.jobId).map(d => ({
        key: `doc${d.id}`, type: 'document', leaf: true,
        label: String(d.label || d.caption || d.file_name),
        sub: String(d.document_category || ''),
        fileUrl: `/api/documents/${d.id}/render`,
      }));
    }
    return [];
  };

  const toggle = async (n: Node) => {
    setSelected(n);
    if (n.leaf) {
      // Records open as desktop windows too — the folder window stays put (#75)
      if (n.href) openWindow({ route: n.href, title: n.label, icon: ICON[n.type] });
      else if (n.fileUrl) {
        const tk = localStorage.getItem('accessToken');
        fetch(n.fileUrl, { headers: { Authorization: `Bearer ${tk}` } })
          .then(res => res.ok ? res.blob() : Promise.reject())
          .then(b => window.open(URL.createObjectURL(b), '_blank')).catch(() => {});
      }
      return;
    }
    if (n.open) { mutate(n.key, x => ({ ...x, open: false })); return; }
    if (n.children) { mutate(n.key, x => ({ ...x, open: true })); return; }
    mutate(n.key, x => ({ ...x, loading: true }));
    const kids = await loadChildren(n);
    mutate(n.key, x => ({ ...x, loading: false, open: true, children: kids }));
  };

  const Row = ({ n, depth }: { n: Node; depth: number }) => (
    <>
      <button onClick={() => toggle(n)}
        className={`w-full flex items-center gap-1.5 py-1.5 px-2 rounded-lg text-left hover:bg-white/60 ${selected?.key === n.key ? 'bg-white/80' : ''}`}
        style={{ paddingLeft: depth * 18 + 8 }}>
        {!n.leaf && (
          <span className="material-symbols-outlined text-gray-400 text-base w-4">
            {n.loading ? 'progress_activity' : n.open ? 'expand_more' : 'chevron_right'}
          </span>
        )}
        {n.leaf && <span className="w-4" />}
        <span className={`material-symbols-outlined text-lg ${n.leaf ? 'text-gray-500' : 'text-[#C77D3F]'}`}>{ICON[n.type]}</span>
        <span className="flex-1 min-w-0 truncate text-sm text-gray-800">{n.label}</span>
        {n.sub && <span className="text-[11px] text-gray-400 truncate hidden sm:inline">{n.sub}</span>}
      </button>
      {n.open && n.children?.map(c => <Row key={c.key} n={c} depth={depth + 1} />)}
      {n.open && n.children?.length === 0 && (
        <p className="text-[11px] text-gray-400 italic py-1" style={{ paddingLeft: (depth + 1) * 18 + 28 }}>empty</p>
      )}
    </>
  );

  return (
    <MainLayout>
      {/* Wallpaper desktop (#72) */}
      <div className="relative rounded-2xl p-3 sm:p-6 min-h-[78vh] overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #2C1810 0%, #5a3a24 60%, #C77D3F 140%)' }}>
        {/* Launcher icons — apps open as real desktop windows (#75); the whole
            desktop stays underneath. Double up: single click = window. */}
        <div className="absolute top-4 left-4 grid grid-cols-1 gap-3 w-20">
          {LAUNCHERS.map(l => (
            <button key={l.label}
              onClick={() => l.href === '/explorer'
                ? setMinimized(false)
                : openWindow({ route: l.href, title: l.label, icon: l.icon })}
              title={l.href === '/explorer' ? 'Show the folder window' : `Open ${l.label} in a window`}
              className="flex flex-col items-center gap-1 text-white/90 hover:text-white group">
              <span className="h-12 w-12 rounded-xl bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center group-hover:bg-white/20">
                <span className="material-symbols-outlined text-2xl">{l.icon}</span>
              </span>
              <span className="text-[10px] font-medium drop-shadow">{l.label}</span>
            </button>
          ))}
        </div>

        {/* Explorer window */}
        {!minimized && (
          <div className="relative bg-white/95 dark:bg-[#241D18]/95 rounded-xl shadow-2xl overflow-hidden w-full max-w-2xl mx-auto flex flex-col"
            style={{
              ...(pos ? { transform: `translate(${pos.x}px, ${pos.y}px)` } : {}),
              ...(size ? { width: size.w, height: size.h, maxWidth: 'none' } : {}),
            }}>
            <div onPointerDown={onTitleDown} onPointerMove={onTitleMove} onPointerUp={onTitleUp}
              className="flex items-center gap-2 px-4 py-2 bg-[#FAF6F0] border-b border-gray-200 cursor-move select-none touch-none">
              <button onClick={() => setMinimized(true)} title="Minimize" className="h-3 w-3 rounded-full bg-[#D69E2E]" />
              <span className="h-3 w-3 rounded-full bg-[#5C7F4F]" />
              <span className="ml-2 text-xs font-semibold text-gray-600 flex items-center gap-1">
                <span className="material-symbols-outlined text-base text-[#C77D3F]">folder_open</span>
                OchrePoint — File Explorer
              </span>
              {pos && <button onClick={() => setPos(null)} className="ml-auto text-[10px] text-gray-400 hover:text-gray-700">reset</button>}
            </div>
            <div className={`p-2 overflow-y-auto bg-[#FAF6F0]/40 ${size ? 'flex-1' : 'max-h-[62vh]'}`}>
              {roots.length === 0 ? (
                <p className="text-sm text-gray-400 p-4">Loading clients…</p>
              ) : roots.map(n => <Row key={n.key} n={n} depth={0} />)}
            </div>
            <p className="text-[10px] text-gray-400 px-4 py-1.5 border-t border-gray-100 flex items-center gap-1">
              Drag the title bar to move · corner to resize · minimize to the taskbar.
              {size && <button onClick={() => setSize(null)} className="ml-auto text-gray-400 hover:text-gray-700">reset size</button>}
            </p>
            {/* Resize handle */}
            <div onPointerDown={onResizeDown} onPointerMove={onResizeMove} onPointerUp={onResizeUp}
              title="Drag to resize"
              className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize touch-none text-gray-400 hover:text-[#C77D3F] flex items-end justify-end">
              <span className="material-symbols-outlined text-base leading-none">resize</span>
            </div>
          </div>
        )}

        {/* Taskbar */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/30 backdrop-blur border border-white/15 rounded-full px-3 py-1.5">
          <button onClick={() => setMinimized(m => !m)}
            className={`flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold ${minimized ? 'text-white/70 hover:text-white' : 'bg-[#C77D3F] text-white'}`}>
            <span className="material-symbols-outlined text-base">folder_open</span>
            File Explorer{minimized ? ' (minimized)' : ''}
          </button>
        </div>
      </div>
    </MainLayout>
  );
};
